/**
 * useSlashCommandKeys — the keyboard of the slash-command panel, for the thread's composer input
 * (`composerInputProps.onKeyDown`). While the typed text starts with a slash command:
 *
 *   - Esc and Ctrl/Cmd+Enter apply the command (the options stay chosen, the command text goes);
 *   - ArrowUp / ArrowDown move the highlighted option;
 *   - Enter picks the highlighted option (`/indicator`, `/strategy`, `/ticker`, `/timeframe`) or, for `/research`,
 *     keeps the objective;
 *
 * each with `preventDefault()`, so Enter never sends the raw `/strategy x` text. Outside a command the handler does
 * nothing and the thread's own Enter-to-send (and Ctrl+Enter) runs. The panel reads `highlightIndex` and reports the
 * options it lists through `onOptionsChange`; it registers what "apply" means through `applyRef`.
 */
import { type KeyboardEvent, type MutableRefObject, useCallback, useRef, useState } from 'react';
import { type ConversationCommandsActions, detectSlashCommand } from '../../hooks/useConversationCommands.js';
import type { FinanceCommandLookupOption } from '../../generated/api/types.gen.js';

export interface SlashCommandKeys {
  highlightIndex: number;
  onOptionsChange: (options: FinanceCommandLookupOption[]) => void;
  /** Back to the first option (the search text changed). */
  resetHighlight: () => void;
  /** The panel sets this to its "apply" action. */
  applyRef: MutableRefObject<(() => void) | null>;
  onKeyDown: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
}

export function useSlashCommandKeys(
  toggleSelection: ConversationCommandsActions['toggleSelection'] | null,
): SlashCommandKeys {
  const [options, setOptions] = useState<FinanceCommandLookupOption[]>([]);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const applyRef = useRef<(() => void) | null>(null);

  const onOptionsChange = useCallback((next: FinanceCommandLookupOption[]) => {
    setOptions(next);
    setHighlightIndex((current) => (next.length === 0 ? 0 : Math.min(current, next.length - 1)));
  }, []);
  const resetHighlight = useCallback(() => setHighlightIndex(0), []);

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (!toggleSelection || event.nativeEvent.isComposing) return;
    const command = detectSlashCommand(event.currentTarget.value);
    if (!command) return;

    const handled = () => {
      event.preventDefault();
      // Esc must not also close a side panel that listens on the window.
      event.stopPropagation();
    };

    if (event.key === 'Escape' || (event.key === 'Enter' && (event.ctrlKey || event.metaKey))) {
      handled();
      applyRef.current?.();
      return;
    }
    if (command.command === 'research') {
      if (event.key === 'Enter' && !event.shiftKey) {
        handled();
        applyRef.current?.();
      }
      return;
    }
    if (event.key === 'ArrowDown') {
      handled();
      setHighlightIndex((current) => (options.length === 0 ? 0 : Math.min(options.length - 1, current + 1)));
    } else if (event.key === 'ArrowUp') {
      handled();
      setHighlightIndex((current) => Math.max(0, current - 1));
    } else if (event.key === 'Enter' && !event.shiftKey) {
      handled();
      const option = options[highlightIndex];
      if (option) toggleSelection(option);
    }
  };

  return { highlightIndex, onOptionsChange, resetHighlight, applyRef, onKeyDown };
}
